/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, GearIcon } from '@/shared/ui/radix-icons';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { MemberType } from '@pages/authenticated/agency-pages/AgenciesContext';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { memberColors, VISITS_COLOR } from '@features/agenda/utils/agenda-grouping';
import { MiniCalendar } from './MiniCalendar';
import { SectionLabel, CalendarRow, MemberRow, Swatch } from './AgendaSidebar.rows';
import { Label, Text, UnstyledButton, NativeCheckbox } from '@/shared/ui';

interface AgendaSidebarProps {
  calendars: Calendar[];
  allCalendars: Calendar[];
  activeCalendars: Set<string>;
  onToggleCalendar: (calendarId: string) => void;
  anchor: Date;
  onSelectDate: (date: Date) => void;
  agencyName?: string;
  members: MemberType[];
  activeMembers: Set<string>;
  onToggleMember: (email: string) => void;
  canViewMemberAgendas: boolean;
  currentUserEmail: string;
  onCreate?: () => void;
  canCreate?: boolean;
  onManageCalendars?: () => void;
  visitsVisible?: boolean;
  onToggleVisits?: () => void;
}

/**
 * Left sidebar: mini-calendar, calendar toggles, member toggles.
 *
 * @param props Sidebar state and handlers.
 * @returns Rendered sidebar.
 */
export const AgendaSidebar: React.FC<AgendaSidebarProps> = ({
  calendars,
  allCalendars,
  activeCalendars,
  onToggleCalendar,
  anchor,
  onSelectDate,
  agencyName,
  members,
  activeMembers,
  onToggleMember,
  canViewMemberAgendas,
  currentUserEmail,
  onCreate,
  canCreate,
  onManageCalendars,
  visitsVisible = true,
  onToggleVisits,
}) => {
  const { t } = useTranslation('features/agenda');
  // One pass over the whole roster — the signed-in user included, so their own
  // calendar's colour is reserved before anyone else draws from the palette.
  const colors = memberColors(
    [currentUserEmail, ...members.map((m) => m.email)].filter(Boolean),
    allCalendars,
  );

  return (
    <aside data-tour="agenda-sidebar" className="flex h-full w-[220px] flex-shrink-0 flex-col bg-[var(--surface-primary)] p-(--space-3)">
      {canCreate && onCreate && (
        <UnstyledButton
          type="button"
          data-testid="sidebar-create-btn"
          onClick={onCreate}
          className="mb-(--space-3) flex w-full items-center justify-center gap-(--space-2) rounded-[var(--radius-md)] border-none px-(--space-4) py-(--space-2-5) [font-weight:var(--font-bold)] text-[var(--button-primary-text)] transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--seed-base)] focus-visible:ring-offset-1"
          style={{ background: 'var(--seed-base)', fontSize: 'var(--text-small)' }}
        >
          <PlusIcon width={13} height={13} />
          {t('toolbar.new_event')}
        </UnstyledButton>
      )}

      <MiniCalendar anchor={anchor} onSelectDate={onSelectDate} />

      {/* Scrollable area: calendars + members */}
      <div className="mt-(--space-3) flex min-h-0 flex-1 flex-col gap-(--space-3) overflow-x-hidden overflow-y-auto">
      <div className="flex flex-col gap-(--space-1)">
        <div className="flex items-center justify-between">
          <SectionLabel label={t('sidebar.section.mine')} />
          {onManageCalendars && (
            <UnstyledButton
              type="button"
              data-testid="sidebar-manage-calendars-btn"
              onClick={onManageCalendars}
              aria-label={t('sidebar.manage_calendars')}
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-primary)] text-[var(--text-secondary)] shadow-sm transition-all hover:border-[var(--border-default)] hover:bg-[var(--surface-secondary)] hover:text-[var(--text-primary)] active:scale-95"
            >
              <GearIcon width={15} height={15} />
            </UnstyledButton>
          )}
        </div>
        {calendars.length > 0 ? (
          <div className="flex flex-col gap-(--space-0-5)">
            {calendars.map((cal) => (
              <CalendarRow
                key={cal.calendar_id}
                calendar={cal}
                active={cal.calendar_id ? activeCalendars.has(cal.calendar_id) : false}
                onToggle={onToggleCalendar}
                displayName={calendarDisplayName(cal, t, agencyName)}
              />
            ))}
            {/* Visitas — toggleable layer */}
            <Label className="flex cursor-pointer items-center gap-(--space-2) rounded-[var(--radius-sm)] px-(--space-1-5) py-(--space-1) transition-colors hover:bg-[var(--surface-secondary)]">
              <NativeCheckbox
                
                className="sr-only"
                checked={visitsVisible}
                onChange={onToggleVisits}
                data-testid="sidebar-visits-toggle"
              />
              <Swatch
                active={visitsVisible}
                color={VISITS_COLOR}
                className="rounded-(--radius-sm)"
              />
              <Text component="span" variant="small" className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[var(--text-primary)]" style={{ fontWeight: 500 }}>
                {t('sidebar.visits')}
              </Text>
            </Label>
          </div>
        ) : (
          <Text component="span" className="block px-(--space-1) text-xs font-normal text-[var(--text-tertiary)]">
            {t('sidebar.no_personal_calendars')}
          </Text>
        )}
      </div>

      {/* ── Seção: Membros (operacional+) ── */}
      {canViewMemberAgendas && (
        <div className="flex flex-col gap-(--space-1)">
          <SectionLabel label={t('sidebar.section.members_short')} />
          {members.length > 0 ? (
            <div className="flex flex-col gap-(--space-0-5)">
              {members.map((m) => (
                <MemberRow
                  key={m.email}
                  member={m}
                  active={activeMembers.has(m.email)}
                  onToggle={onToggleMember}
                  color={colors.get(m.email)}
                />
              ))}
            </div>
          ) : (
            <Text component="span" className="block px-(--space-1) text-xs font-normal text-[var(--text-tertiary)]">
              {t('sidebar.no_members')}
            </Text>
          )}
        </div>
      )}

      </div>
    </aside>
  );
};
